import {
	Dtos,
	type SfaaClassExperienceImportStatus as ImportStatus,
	SfaaClassExperienceImportStatus,
} from "@inrev/common";
import {
	findInFlightSfaaImport,
	SFAA_CLASS_EXPERIENCE_IMPORT_MUTATION_KEY,
	useDeleteSfaaClassExperienceDataset,
	useFetchSfaaClassExperienceImports,
	useImportSfaaClassExperience,
} from "@portal/api/sfaa-class-experience/admin";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { EmptyState, InProgressBanner } from "@portal/components/patterns";
import { Dropzone } from "@portal/components/patterns/dropzone";
import {
	Banner,
	Button,
	Card,
	Icon,
	Spinner,
	Text,
	type TextColor,
} from "@portal/components/primitives";
import { useIsMutating } from "@tanstack/react-query";

const { isImportInFlight } = Dtos.Admin.SfaaClassExperience;

const SFAA_FILE_ACCEPT = {
	"text/csv": [".csv"],
	"application/vnd.ms-excel": [".xls"],
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

const statusColor: Record<ImportStatus, TextColor> = {
	[SfaaClassExperienceImportStatus.Pending]: "secondary",
	[SfaaClassExperienceImportStatus.Processing]: "warning",
	[SfaaClassExperienceImportStatus.Succeeded]: "confirm",
	[SfaaClassExperienceImportStatus.Failed]: "danger",
};

/** A stale import may still say `processing`, but it has stopped heartbeating and is terminal. */
const statusOf = (
	importRecord: Dtos.Admin.SfaaClassExperience.Import.Response,
): { label: string; color: TextColor } =>
	importRecord.isStale
		? { label: "Interrupted", color: "danger" }
		: {
				label: SfaaClassExperienceImportStatus.displayName(importRecord.status),
				color: statusColor[importRecord.status],
			};

const formatImportedAt = (value: Dtos.Admin.SfaaClassExperience.Import.Response["createdAt"]) =>
	new Intl.DateTimeFormat(undefined, {
		dateStyle: "medium",
		timeStyle: "short",
	}).format(new Date(value.toString()));

const ImportHistory = ({
	imports,
	onDelete,
	isDeleting,
}: {
	imports: Dtos.Admin.SfaaClassExperience.Import.Response[];
	onDelete: (id: Dtos.Admin.SfaaClassExperience.Import.Response["id"]) => void;
	isDeleting: boolean;
}) => {
	if (imports.length === 0) {
		return (
			<EmptyState
				className="py-10"
				testId="bulk-load-empty-state"
				message="No class experience files have been imported."
			/>
		);
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse">
				<thead>
					<tr className="border-b border-bc-default bg-surface-light">
						{["File", "Status", "File rows", "Live rows", "Imported", "Actions"].map((heading) => (
							<th key={heading} scope="col" className="px-4 py-3 text-left">
								<Text type="caption" color="secondary">
									{heading}
								</Text>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{imports.map((importRecord) => (
						<tr key={importRecord.id} className="border-b border-bc-default last:border-b-0">
							<td className="px-4 py-3">
								<div className="flex min-w-0 items-center gap-2">
									<Icon type="document-chart-bar" size="small" color="secondary" />
									<div className="flex min-w-0 flex-col gap-1.5">
										<Text type="body-strong" color="primary" truncate>
											{importRecord.originalFilename}
										</Text>
										{importRecord.errorMessage ? (
											<Text type="caption" color="danger">
												{importRecord.errorMessage}
											</Text>
										) : null}
									</div>
								</div>
							</td>
							<td className="px-4 py-3">
								<div className="flex items-center gap-2">
									{isImportInFlight(importRecord.status) && !importRecord.isStale ? (
										<Spinner size="md" className="text-secondary" />
									) : null}
									<Text type="body-strong" color={statusOf(importRecord).color}>
										{statusOf(importRecord).label}
									</Text>
								</div>
							</td>
							<td className="px-4 py-3">
								<Text type="body" color="secondary">
									{importRecord.rowCount?.toLocaleString() ?? "—"}
								</Text>
							</td>
							<td className="px-4 py-3">
								<Text
									type="body"
									color={importRecord.attributedRowCount > 0 ? "primary" : "tertiary"}
								>
									{importRecord.attributedRowCount.toLocaleString()}
								</Text>
							</td>
							<td className="px-4 py-3">
								<Text type="body" color="secondary">
									{formatImportedAt(importRecord.createdAt)}
								</Text>
							</td>
							<td className="px-4 py-3">
								<ConfirmationModalClickWrapper
									message="Delete this import?"
									subMessage={`The ${importRecord.attributedRowCount.toLocaleString()} row(s) it currently backs will be removed. Any row it replaced from an earlier import is restored to that import's values.`}
									confirmButtonText="Delete"
									confirmationButtonColor="danger"
									confirmTrackingId="sfaa-class-experience-delete-confirm"
									onConfirm={() => onDelete(importRecord.id)}
								>
									<Button
										color="danger"
										variant="outline"
										size="sm"
										disabled={isDeleting || isImportInFlight(importRecord.status)}
										leadingIcon={<Icon type="trash" size="micro" color="danger" />}
										trackingId="sfaa-class-experience-delete"
									>
										Delete
									</Button>
								</ConfirmationModalClickWrapper>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export const BulkLoadTab = () => {
	const importsQuery = useFetchSfaaClassExperienceImports();
	const importMutation = useImportSfaaClassExperience();
	const deleteMutation = useDeleteSfaaClassExperienceDataset();

	// Server state survives navigation; mutation state covers the gap before the import record exists.
	const isUploading = useIsMutating({ mutationKey: SFAA_CLASS_EXPERIENCE_IMPORT_MUTATION_KEY }) > 0;
	const inFlightImport = findInFlightSfaaImport(importsQuery.data);
	const isImporting = isUploading || inFlightImport !== undefined;

	const handleDrop = (files: File[]) => {
		const file = files[0];
		if (file) importMutation.mutate(file);
	};

	return (
		<div className="flex h-full flex-col gap-6 overflow-auto">
			<Card>
				<Card.Header>
					<Card.Title>Load class experience data</Card.Title>
				</Card.Header>
				<Card.Body className="flex flex-col gap-4 p-6">
					<Text type="body" color="secondary">
						Upload a CSV or Excel file to add or update SFAA class experience data.
					</Text>
					<Dropzone
						accept={SFAA_FILE_ACCEPT}
						disabled={isImporting}
						disabledMessage="An import is in progress"
						onDrop={handleDrop}
						placeholder="Upload SFAA class data"
						testId="sfaa-class-experience-dropzone"
						trackingId="sfaa-class-experience-import"
					/>
					{isImporting ? (
						<InProgressBanner testId="sfaa-class-experience-import-progress">
							<Text type="body" color="primary">
								{inFlightImport === undefined
									? "Uploading file…"
									: `Importing ${inFlightImport.originalFilename}.`}{" "}
								This keeps running if you leave this tab — the import history below updates when it
								finishes.
							</Text>
						</InProgressBanner>
					) : null}
					{importMutation.isError ? (
						<Banner tone="danger">
							<Banner.Leading>
								<Icon type="exclamation-circle" size="small" color="danger" />
							</Banner.Leading>
							<Banner.Content>
								<Text type="body" color="danger">
									{importMutation.error instanceof Error
										? importMutation.error.message
										: "The file could not be imported."}
								</Text>
							</Banner.Content>
						</Banner>
					) : null}
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>
					<Card.Title>Import history</Card.Title>
				</Card.Header>
				<Card.Body className="p-0">
					{deleteMutation.isError ? (
						<Banner tone="danger" className="m-4 w-auto">
							<Banner.Content>
								<Text type="body" color="danger">
									The imported dataset could not be deleted.
								</Text>
							</Banner.Content>
						</Banner>
					) : null}
					{importsQuery.isLoading ? (
						<div className="flex justify-center py-10">
							<Spinner />
						</div>
					) : importsQuery.isError ? (
						<Banner tone="danger" className="m-4 w-auto">
							<Banner.Content>
								<Text type="body" color="danger">
									Import history could not be loaded.
								</Text>
							</Banner.Content>
						</Banner>
					) : (
						<ImportHistory
							imports={importsQuery.data ?? []}
							onDelete={deleteMutation.mutate}
							isDeleting={deleteMutation.isPending}
						/>
					)}
				</Card.Body>
			</Card>
		</div>
	);
};
